import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import type { Order, Gift } from '../lib/types'
import { RECIPIENT_TYPE_LABELS, YEAR_GROUP_LABELS } from '../lib/types'
import { ConfirmModal } from '../components/ui/Modal'

const YEAR_GROUPS = [
  { value: 'reception', label: 'Reception' },
  { value: 'year_1', label: 'Year 1' },
  { value: 'year_2', label: 'Year 2' },
  { value: 'year_3', label: 'Year 3' },
  { value: 'year_4', label: 'Year 4' },
  { value: 'year_5', label: 'Year 5' },
  { value: 'year_6', label: 'Year 6' },
]

const RECIPIENT_TYPES = [
  { value: 'adult_male', label: 'Adult Male' },
  { value: 'adult_female', label: 'Adult Female' },
  { value: 'child_5_under', label: 'Child (5 and under)' },
  { value: 'child_6_over', label: 'Child (6 and over)' },
  { value: 'pet_cat', label: 'Pet Cat' },
  { value: 'pet_dog', label: 'Pet Dog' },
  { value: 'foodbank', label: 'Foodbank Donation' },
]

export function EditOrderPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Form state for order details
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')

  // State for editing gifts
  const [editingGiftId, setEditingGiftId] = useState<string | null>(null)
  const [editingGift, setEditingGift] = useState<Partial<Gift>>({})

  // State for editing Santa booking
  const [editingSantaBooking, setEditingSantaBooking] = useState(false)
  const [santaChildren, setSantaChildren] = useState<string[]>([])
  const [santaDietary, setSantaDietary] = useState('')

  // State for editing tokens
  const [editingTokens, setEditingTokens] = useState(false)
  const [tokenQuantity, setTokenQuantity] = useState(0)

  // State for editing notes
  const [editingNotes, setEditingNotes] = useState(false)
  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (id) {
      loadOrder(true)
    }
  }, [id])

  // Only the first load shows the full-page spinner; reloads after a save keep the page, scroll and focus.
  async function loadOrder(initial = false) {
    if (!id) return

    try {
      if (initial) setLoading(true)
      setError(null)
      const data = await api.getAdminOrder(id)
      setOrder(data)
      setEmail(data.email)
      setFullName(data.fullName)

      // Parse Santa booking data if exists
      if (data.santaBooking) {
        setSantaChildren(JSON.parse(data.santaBooking.childrenNames))
        setSantaDietary(data.santaBooking.dietaryRequirements || '')
      }

      // Set token quantity
      setTokenQuantity(data.tokenQuantity || 0)

      // Set notes
      setNotes(data.notes || '')
    } catch (err) {
      setError('Failed to load order')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  async function saveOrderDetails() {
    if (!id) return

    try {
      setSaving(true)
      setError(null)
      setSuccessMessage(null)

      await api.updateAdminOrder(id, {
        email,
        fullName,
        wantsGifts: order?.wantsGifts || false,
        wantsSanta: order?.wantsSanta || false,
      })

      setSuccessMessage('Order details updated successfully')
      await loadOrder()

      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to update order details')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  function startEditingGift(gift: Gift) {
    setEditingGiftId(gift.id)
    setEditingGift(gift)
  }

  function cancelEditingGift() {
    setEditingGiftId(null)
    setEditingGift({})
  }

  async function saveGift() {
    if (!editingGiftId || !editingGift) return

    try {
      setSaving(true)
      setError(null)

      await api.updateAdminGift(editingGiftId, {
        recipientType: editingGift.recipientType!,
        recipientName: editingGift.recipientName!,
        fromName: editingGift.fromName!,
        yearGroup: editingGift.yearGroup!,
        pickupLocation: editingGift.pickupLocation!,
      })

      setSuccessMessage('Gift updated successfully')
      setEditingGiftId(null)
      setEditingGift({})
      await loadOrder()

      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to update gift')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  async function deleteGift(giftId: string) {
    if (!confirm('Are you sure you want to delete this gift?')) return

    try {
      setSaving(true)
      setError(null)

      await api.deleteAdminGift(giftId)

      setSuccessMessage('Gift deleted successfully')
      await loadOrder()

      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to delete gift')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  async function saveSantaBooking() {
    if (!order?.santaBooking?.id) return

    try {
      setSaving(true)
      setError(null)

      await api.updateAdminSantaBooking(order.santaBooking.id, {
        childrenNames: santaChildren,
        dietaryRequirements: santaDietary,
      })

      setSuccessMessage('Santa booking updated successfully')
      setEditingSantaBooking(false)
      await loadOrder()

      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to update Santa booking')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  async function saveTokens() {
    if (!id) return

    try {
      setSaving(true)
      setError(null)

      await api.updateTokenQuantity(id, tokenQuantity)

      setSuccessMessage('Token quantity updated successfully')
      setEditingTokens(false)
      await loadOrder()

      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to update token quantity')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  async function saveNotes() {
    if (!id) return

    try {
      setSaving(true)
      setError(null)

      await api.updateAdminOrderNotes(id, notes)

      setSuccessMessage('Notes updated successfully')
      setEditingNotes(false)
      await loadOrder()

      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError('Failed to update notes')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  async function deleteOrder() {
    if (!id) return

    try {
      setDeleting(true)
      setError(null)

      await api.deleteAdminOrder(id)

      // Navigate back to orders page
      navigate('/admin/orders')
    } catch (err) {
      setError('Failed to delete order')
      console.error(err)
      setShowDeleteModal(false)
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-gray-600">Loading order...</div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="p-6">
        <div role="alert" className="text-red-600">{error || 'Order not found'}</div>
        <button
          onClick={() => navigate('/admin/orders')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Back to Orders
        </button>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Edit Order #{order.orderNumber ? order.orderNumber.toString().padStart(3, '0') : '---'}</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowDeleteModal(true)}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Delete Order
          </button>
          <button
            onClick={() => navigate('/admin/orders')}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Back to Orders
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={deleteOrder}
        title="Delete Order?"
        message={
          <>
            <p className="mb-4">
              Are you sure you want to delete this order? This action cannot be undone.
            </p>
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-700"><strong>Order #:</strong> {order.orderNumber ? order.orderNumber.toString().padStart(3, '0') : '---'}</p>
              <p className="text-sm text-gray-700"><strong>Customer:</strong> {order.fullName}</p>
              <p className="text-sm text-gray-700"><strong>Email:</strong> {order.email}</p>
              <p className="text-sm text-gray-700"><strong>Total:</strong> £{order.totalAmount.toFixed(2)}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800 font-semibold mb-1">This will delete:</p>
              <ul className="text-sm text-red-700 list-disc list-inside space-y-0.5">
                {order.gifts && order.gifts.length > 0 && (
                  <li>{order.gifts.length} gift{order.gifts.length > 1 ? 's' : ''}</li>
                )}
                {order.santaBooking && (
                  <li>Santa booking (slot will be released)</li>
                )}
                {order.tokenQuantity > 0 && (
                  <li>{order.tokenQuantity} activity token{order.tokenQuantity > 1 ? 's' : ''}</li>
                )}
                <li>All order records</li>
              </ul>
            </div>
          </>
        }
        confirmText="Yes, Delete Order"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        loading={deleting}
      />

      {error && (
        <div role="alert" className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {successMessage && (
        <div role="status" className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {successMessage}
        </div>
      )}

      {/* Order Details */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Order Details</h2>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="order-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              id="order-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="order-full-name" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              id="order-full-name"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={saveOrderDetails}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Save Order Details
          </button>
        </div>
      </div>

      {/* Gifts */}
      {order.gifts && order.gifts.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Gifts ({order.gifts.length})</h2>

          <div className="space-y-4">
            {order.gifts.map((gift) => (
              <div key={gift.id} className="border border-gray-200 rounded p-4">
                {editingGiftId === gift.id ? (
                  <div className="space-y-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <label htmlFor="gift-recipient-type" className="block text-sm font-medium text-gray-700 mb-1">Recipient Type</label>
                        <select
                          id="gift-recipient-type"
                          value={editingGift.recipientType}
                          onChange={(e) => setEditingGift({ ...editingGift, recipientType: e.target.value as any })}
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {RECIPIENT_TYPES.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="gift-recipient-name" className="block text-sm font-medium text-gray-700 mb-1">Recipient Name</label>
                        <input
                          id="gift-recipient-name"
                          type="text"
                          value={editingGift.recipientName}
                          onChange={(e) => setEditingGift({ ...editingGift, recipientName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label htmlFor="gift-from-name" className="block text-sm font-medium text-gray-700 mb-1">From Name</label>
                        <input
                          id="gift-from-name"
                          type="text"
                          value={editingGift.fromName}
                          onChange={(e) => setEditingGift({ ...editingGift, fromName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label htmlFor="gift-year-group" className="block text-sm font-medium text-gray-700 mb-1">Year Group</label>
                        <select
                          id="gift-year-group"
                          value={editingGift.yearGroup}
                          onChange={(e) => setEditingGift({ ...editingGift, yearGroup: e.target.value as any })}
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {YEAR_GROUPS.map(year => (
                            <option key={year.value} value={year.value}>{year.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="gift-pickup-location" className="block text-sm font-medium text-gray-700 mb-1">Pickup Location</label>
                        <select
                          id="gift-pickup-location"
                          value={editingGift.pickupLocation}
                          onChange={(e) => setEditingGift({ ...editingGift, pickupLocation: e.target.value as any })}
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="school">School</option>
                          <option value="fayre">Fayre</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={saveGift}
                        disabled={saving}
                        className="min-h-[44px] px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEditingGift}
                        disabled={saving}
                        className="min-h-[44px] px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-gray-600">
                          <strong>For:</strong> {gift.recipientName} ({RECIPIENT_TYPE_LABELS[gift.recipientType]})
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>From:</strong> {gift.fromName}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Year Group:</strong> {YEAR_GROUP_LABELS[gift.yearGroup]}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Pickup:</strong> {gift.pickupLocation}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Price:</strong> £{gift.price.toFixed(2)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEditingGift(gift)}
                          className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteGift(gift.id)}
                          disabled={saving}
                          className="min-h-[44px] px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Santa Booking */}
      {order.santaBooking && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Santa Booking</h2>

          {editingSantaBooking ? (
            <div className="space-y-3">
              <div>
                <label htmlFor="santa-children" className="block text-sm font-medium text-gray-700 mb-1">Children Names (comma-separated)</label>
                <input
                  id="santa-children"
                  type="text"
                  value={santaChildren.join(', ')}
                  onChange={(e) => setSantaChildren(e.target.value.split(',').map(s => s.trim()))}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="santa-dietary" className="block text-sm font-medium text-gray-700 mb-1">Dietary Requirements</label>
                <textarea
                  id="santa-dietary"
                  value={santaDietary}
                  onChange={(e) => setSantaDietary(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={saveSantaBooking}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingSantaBooking(false)}
                  disabled={saving}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Children:</strong> {JSON.parse(order.santaBooking.childrenNames).join(', ')}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Time Slot:</strong> {order.santaBooking.timeSlot?.time} (Slot {order.santaBooking.timeSlot?.slotNumber})
              </p>
              {order.santaBooking.dietaryRequirements && (
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Dietary Requirements:</strong> {order.santaBooking.dietaryRequirements}
                </p>
              )}

              <button
                onClick={() => setEditingSantaBooking(true)}
                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Edit Santa Booking
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tokens */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Activity Tokens</h2>

        {editingTokens ? (
          <div className="space-y-3">
            <div>
              <label htmlFor="token-quantity" className="block text-sm font-medium text-gray-700 mb-1">Token Quantity</label>
              <input
                id="token-quantity"
                type="number"
                min="0"
                value={tokenQuantity}
                onChange={(e) => setTokenQuantity(Math.max(0, parseInt(e.target.value) || 0))}
                className="w-32 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={saveTokens}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setEditingTokens(false)
                  setTokenQuantity(order.tokenQuantity || 0)
                }}
                disabled={saving}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-gray-600 mb-2">
              <strong>Tokens:</strong> {order.tokenQuantity || 0}
            </p>

            <button
              onClick={() => setEditingTokens(true)}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Edit Tokens
            </button>
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Notes</h2>

        {editingNotes ? (
          <div className="space-y-3">
            <div>
              <label htmlFor="order-notes" className="block text-sm font-medium text-gray-700 mb-1">Order Notes</label>
              <textarea
                id="order-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this order..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={saveNotes}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setEditingNotes(false)
                  setNotes(order.notes || '')
                }}
                disabled={saving}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            {order.notes ? (
              <p className="text-sm text-gray-600 mb-2 whitespace-pre-wrap">{order.notes}</p>
            ) : (
              <p className="text-sm text-gray-600 mb-2 italic">No notes</p>
            )}

            <button
              onClick={() => setEditingNotes(true)}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              {order.notes ? 'Edit Notes' : 'Add Notes'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
